import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { DEALER_STATUSES, INQUIRY_STATUSES, STATUS_STYLES } from "@/lib/constants";
import { cx } from "@/components/FormFields";
import { ProductsPanel } from "@/components/ProductsPanel";
import { PaymentsPanel } from "@/components/PaymentsPanel";
import { SiteContentPanel } from "@/components/SiteContentPanel";
import { AccountPanel } from "@/components/AccountPanel";

const StatusPill = ({ status }) => (
  <span className={cx("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-semibold whitespace-nowrap", STATUS_STYLES[status] || STATUS_STYLES["New"])}>
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {status}
  </span>
);

const Kpi = ({ label, value, accent }) => (
  <div className="rounded-xl border border-[#232f25] bg-[#111712] p-5">
    <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#69776b]">{label}</div>
    <div className={cx("font-display text-3xl font-black mt-2", accent ? "text-[#8CE63C]" : "text-white")}>{value}</div>
  </div>
);

const DetailRow = ({ label, value }) => {
  if (value == null || value === "" || (Array.isArray(value) && !value.length)) value = "—";
  if (Array.isArray(value)) value = value.join(", ");
  return (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-[#1a211b]">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#69776b] col-span-1">{label}</div>
      <div className="text-sm text-[#f4f7f4] col-span-2 break-words">{value}</div>
    </div>
  );
};

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [catalogBusy, setCatalogBusy] = useState(false);

  const loadCatalog = useCallback(() => {
    api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const uploadCatalog = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please select a PDF file.");
      return;
    }
    setCatalogBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/catalog", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCatalog(data);
      toast.success("Catalog updated.");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setCatalogBusy(false);
    }
  };

  const removeCatalog = async () => {
    if (!window.confirm("Remove the current catalog from the dealer form?")) return;
    try {
      await api.delete("/catalog");
      setCatalog({ available: false });
      toast.success("Catalog removed.");
    } catch {
      toast.error("Could not remove catalog.");
    }
  };

  const viewCatalog = async () => {
    try {
      const res = await api.get("/catalog/download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open catalog.");
    }
  };

  const fmtSize = (b) => (b ? (b > 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.round(b / 1024) + " KB") : "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        api.get("/dealers", { params: { search: search || undefined, status: statusFilter } }),
        api.get("/dealers/stats"),
      ]);
      setDealers(d.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/dealers/${id}/status`, { status });
      setDealers((ds) => ds.map((d) => (d.id === id ? { ...d, status } : d)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
      api.get("/dealers/stats").then((r) => setStats(r.data)).catch(() => {});
      toast.success(`Status → ${status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/dealers/${id}`);
      setSelected(data);
    } catch {
      toast.error("Could not load details.");
    }
  };

  const addNote = async () => {
    if (!noteText.trim() || !selected) return;
    try {
      const { data } = await api.post(`/dealers/${selected.id}/notes`, { text: noteText });
      setSelected((s) => ({ ...s, notes: [...(s.notes || []), data] }));
      setNoteText("");
      toast.success("Note added.");
    } catch {
      toast.error("Could not add note.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/dealers/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "dealer_inquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return iso;
    }
  };

  const [tab, setTab] = useState("dealers");
  const [inqData, setInqData] = useState({ items: [], total: 0, new: 0 });

  useEffect(() => {
    api.get("/inquiries").then((r) => setInqData(r.data)).catch(() => {});
  }, []);

  const input =
    "bg-[#0d120e] border border-[#232f25] rounded-md px-3.5 py-2.5 text-sm text-[#f4f7f4] placeholder:text-[#69776b] outline-none focus:border-[#8CE63C] focus:ring-2 focus:ring-[#8CE63C]/20";

  return (
    <div className="min-h-screen erp-grid-bg">
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-[#0a0d0a]/85 border-b border-[#232f25]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Brand size="md" />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-[#69776b]">{admin?.email}</span>
            <button
              onClick={() => { logout(); navigate("/admin/login"); }}
              className="text-xs font-semibold uppercase tracking-wide text-[#9EABA0] hover:text-[#F87171] transition-colors px-3 py-2"
            >
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-1 mb-6 border-b border-[#232f25]">
          <button data-testid="admin-tab-dealers" onClick={() => setTab("dealers")} className={cx("px-4 py-3 text-sm font-semibold uppercase tracking-wide border-b-2 -mb-px transition-colors", tab === "dealers" ? "border-[#8CE63C] text-[#8CE63C]" : "border-transparent text-[#9EABA0] hover:text-[#f4f7f4]")}>Dealer Applications</button>
          <button data-testid="admin-tab-inquiries" onClick={() => setTab("inquiries")} className={cx("px-4 py-3 text-sm font-semibold uppercase tracking-wide border-b-2 -mb-px transition-colors flex items-center gap-2", tab === "inquiries" ? "border-[#8CE63C] text-[#8CE63C]" : "border-transparent text-[#9EABA0] hover:text-[#f4f7f4]")}>Requests &amp; Contact{inqData.new ? <span className="text-[10px] font-bold bg-[#8CE63C] text-[#080b08] rounded-full px-1.5 py-0.5">{inqData.new}</span> : null}</button>
          <button data-testid="admin-tab-products" onClick={() => setTab("products")} className={cx("px-4 py-3 text-sm font-semibold uppercase tracking-wide border-b-2 -mb-px transition-colors", tab === "products" ? "border-[#8CE63C] text-[#8CE63C]" : "border-transparent text-[#9EABA0] hover:text-[#f4f7f4]")}>Products</button>
          <button data-testid="admin-tab-payments" onClick={() => setTab("payments")} className={cx("px-4 py-3 text-sm font-semibold uppercase tracking-wide border-b-2 -mb-px transition-colors", tab === "payments" ? "border-[#8CE63C] text-[#8CE63C]" : "border-transparent text-[#9EABA0] hover:text-[#f4f7f4]")}>Payments</button>
          <button data-testid="admin-tab-content" onClick={() => setTab("content")} className={cx("px-4 py-3 text-sm font-semibold uppercase tracking-wide border-b-2 -mb-px transition-colors", tab === "content" ? "border-[#8CE63C] text-[#8CE63C]" : "border-transparent text-[#9EABA0] hover:text-[#f4f7f4]")}>Site Content</button>
          <button data-testid="admin-tab-account" onClick={() => setTab("account")} className={cx("px-4 py-3 text-sm font-semibold uppercase tracking-wide border-b-2 -mb-px transition-colors", tab === "account" ? "border-[#8CE63C] text-[#8CE63C]" : "border-transparent text-[#9EABA0] hover:text-[#f4f7f4]")}>Account</button>
        </div>

        {tab === "dealers" && (<>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-black text-white uppercase tracking-tight">Dealer Inquiries</h1>
            <p className="text-sm text-[#69776b] mt-1">Review and manage wholesale dealer applications.</p>
          </div>
          <button
            data-testid="admin-export-csv-button"
            onClick={exportCsv}
            className="px-4 py-2.5 rounded-md bg-[#0d120e] border border-[#232f25] text-sm font-semibold text-[#f4f7f4] hover:border-[#8CE63C]/50 transition-colors"
          >
            Export CSV
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Kpi label="Total Inquiries" value={stats?.total ?? "—"} accent />
          <Kpi label="Pending Review" value={stats?.pending ?? "—"} />
          <Kpi label="Approved Dealers" value={stats?.approved ?? "—"} />
          <Kpi label="Statuses" value={DEALER_STATUSES.length} />
        </div>

        {/* Inventory catalog manager */}
        <div className="rounded-xl border border-[#232f25] bg-[#111712] p-5 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="h-11 w-11 rounded-md bg-[#132717] border border-[#8CE63C]/40 flex items-center justify-center text-[#8CE63C] font-mono text-xs font-bold shrink-0">
                PDF
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-white tracking-tight">Inventory Availability Catalog</h3>
                {catalog?.available ? (
                  <p className="text-xs text-[#9EABA0] mt-0.5">
                    <span className="text-[#8CE63C]">{catalog.filename}</span> · {fmtSize(catalog.size)} · updated{" "}
                    {catalog.updated_at ? new Date(catalog.updated_at).toLocaleDateString() : ""}
                  </p>
                ) : (
                  <p className="text-xs text-[#69776b] mt-0.5">No catalog uploaded. Dealers won't see a download link until you add one.</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {catalog?.available && (
                <>
                  <button
                    data-testid="admin-catalog-view-button"
                    onClick={viewCatalog}
                    className="px-4 py-2.5 rounded-md bg-[#0d120e] border border-[#232f25] text-sm font-semibold text-[#f4f7f4] hover:border-[#8CE63C]/50 transition-colors"
                  >
                    View
                  </button>
                  <button
                    data-testid="admin-catalog-remove-button"
                    onClick={removeCatalog}
                    className="px-4 py-2.5 rounded-md bg-[#0d120e] border border-[#232f25] text-sm font-semibold text-[#FCA5A5] hover:border-[#F87171]/60 transition-colors"
                  >
                    Remove
                  </button>
                </>
              )}
              <label
                data-testid="admin-catalog-upload-label"
                className={cx(
                  "px-4 py-2.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-bold cursor-pointer hover:bg-[#9df04f] transition-colors",
                  catalogBusy && "opacity-50 pointer-events-none"
                )}
              >
                {catalogBusy ? "Uploading…" : catalog?.available ? "Replace PDF" : "Upload PDF"}
                <input
                  data-testid="admin-catalog-file-input"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={uploadCatalog}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company, contact, email, state, ref #…"
            className={cx(input, "flex-1")}
          />
          <select
            data-testid="admin-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={cx(input, "cursor-pointer")}
          >
            <option value="All">All Statuses</option>
            {DEALER_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="rounded-xl border border-[#232f25] bg-[#111712] overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="admin-submissions-table" className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#232f25] text-left">
                  {["Ref #", "Date", "Business", "Contact", "State", "Units", "Timeline", "Status", ""].map((h) => (
                    <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-[#69776b] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-[#69776b]">Loading…</td></tr>
                ) : dealers.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-[#69776b]">No dealer inquiries found.</td></tr>
                ) : (
                  dealers.map((d) => (
                    <tr key={d.id} className="border-b border-[#1a211b] hover:bg-[#0d120e] transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-[#8CE63C] whitespace-nowrap">{d.reference_id}</td>
                      <td className="px-4 py-3 text-[#9EABA0] whitespace-nowrap">{fmtDate(d.created_at)}</td>
                      <td className="px-4 py-3 text-white font-medium">{d.business_name}</td>
                      <td className="px-4 py-3 text-[#9EABA0]">{d.contact_name}</td>
                      <td className="px-4 py-3 text-[#9EABA0]">{d.state}</td>
                      <td className="px-4 py-3 text-[#9EABA0] whitespace-nowrap">{d.quantity_tier}</td>
                      <td className="px-4 py-3 text-[#9EABA0] whitespace-nowrap">{d.first_order_timing}</td>
                      <td className="px-4 py-3">
                        <select
                          data-testid={`admin-status-select-${d.id}`}
                          value={d.status}
                          onChange={(e) => changeStatus(d.id, e.target.value)}
                          className="bg-[#0d120e] border border-[#232f25] rounded-md px-2 py-1 text-xs text-[#f4f7f4] cursor-pointer focus:border-[#8CE63C] outline-none"
                        >
                          {DEALER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          data-testid={`admin-view-submission-${d.id}`}
                          onClick={() => openDetail(d.id)}
                          className="text-xs font-semibold text-[#8CE63C] hover:underline whitespace-nowrap"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>)}

        {tab === "inquiries" && <InquiriesPanel input={input} onCount={(n) => setInqData((d) => ({ ...d, new: n }))} />}
        {tab === "products" && <ProductsPanel input={input} />}
        {tab === "payments" && <PaymentsPanel />}
        {tab === "content" && <SiteContentPanel input={input} />}
        {tab === "account" && <AccountPanel input={input} />}
      </main>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-xl h-full bg-[#0a0d0a] border-l border-[#232f25] overflow-y-auto erp-fade-up">
            <div className="sticky top-0 bg-[#0a0d0a]/95 backdrop-blur border-b border-[#232f25] px-6 py-4 flex items-center justify-between">
              <div>
                <div className="font-mono text-xs text-[#8CE63C]">{selected.reference_id}</div>
                <h2 className="font-display text-xl font-black text-white uppercase tracking-tight">{selected.business_name}</h2>
              </div>
              <button
                data-testid="admin-detail-close-button"
                onClick={() => setSelected(null)}
                className="text-[#9EABA0] hover:text-white text-2xl leading-none px-2"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-5 space-y-6">
              <div className="flex items-center gap-3 flex-wrap">
                <StatusPill status={selected.status} />
                <div className="flex flex-wrap gap-1.5">
                  {DEALER_STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => changeStatus(selected.id, s)}
                      className={cx(
                        "px-2 py-1 rounded text-[10px] font-semibold border transition-colors",
                        selected.status === s
                          ? "bg-[#8CE63C] border-[#8CE63C] text-[#080b08]"
                          : "bg-[#0d120e] border-[#232f25] text-[#9EABA0] hover:border-[#8CE63C]/50"
                      )}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <Section title="Business Information">
                <DetailRow label="Submitted" value={new Date(selected.created_at).toLocaleString()} />
                <DetailRow label="Contact" value={selected.contact_name} />
                <DetailRow label="Email" value={selected.email} />
                <DetailRow label="Phone" value={selected.phone} />
                <DetailRow label="Address" value={selected.business_address} />
                <DetailRow label="Location" value={`${selected.city}, ${selected.state} ${selected.zip_code}`} />
                <DetailRow label="Website" value={selected.website} />
                <DetailRow label="Social / Page" value={selected.social_media} />
                <DetailRow label="Business Type" value={selected.business_type} />
              </Section>

              <Section title="Dealer Experience">
                <DetailRow label="Sells E-Bikes" value={selected.sells_ebikes} />
                <DetailRow label="Brands Carried" value={selected.current_brands} />
                <DetailRow label="Physical Location" value={selected.physical_location} />
                <DetailRow label="Sells Online" value={selected.sells_online} />
                <DetailRow label="Years in Business" value={selected.years_in_business} />
              </Section>

              <Section title="Products & Quantity">
                <DetailRow label="Products" value={selected.products_interested} />
                <DetailRow label="Quantity" value={selected.quantity_tier} />
                <DetailRow label="Recurring" value={selected.recurring_orders} />
                <DetailRow label="Specific Models" value={selected.interested_specific_models} />
                <DetailRow label="Models List" value={selected.models_list} />
                <DetailRow label="Colors / Specs" value={selected.color_specs} />
                <DetailRow label="Custom Branding" value={selected.custom_branding} />
              </Section>

              <Section title="Timeline & Shipping">
                <DetailRow label="First Order" value={selected.first_order_timing} />
                <DetailRow label="Purchase Type" value={selected.purchase_type} />
                <DetailRow label="Ship To" value={`${selected.shipping_city}, ${selected.shipping_state} ${selected.shipping_zip}`} />
                <DetailRow label="Receiving" value={selected.receiving_location} />
                <DetailRow label="Forklift" value={selected.forklift_access} />
              </Section>

              <Section title="Additional Notes">
                <p className="text-sm text-[#f4f7f4] whitespace-pre-wrap py-2">{selected.additional_info || "—"}</p>
              </Section>

              <Section title="Internal Notes">
                <div className="space-y-2 mb-3">
                  {(selected.notes || []).length === 0 && <p className="text-xs text-[#69776b]">No internal notes yet.</p>}
                  {(selected.notes || []).map((n, i) => (
                    <div key={i} className="rounded-md border border-[#232f25] bg-[#0d120e] p-3">
                      <p className="text-sm text-[#f4f7f4]">{n.text}</p>
                      <p className="text-[10px] text-[#69776b] mt-1 font-mono">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addNote()}
                    placeholder="Add an internal note…"
                    className={cx(input, "flex-1")}
                  />
                  <button
                    data-testid="admin-add-note-button"
                    onClick={addNote}
                    className="px-4 py-2 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-bold hover:bg-[#9df04f] transition-colors"
                  >
                    Add
                  </button>
                </div>
              </Section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <h3 className="font-display text-sm font-bold uppercase tracking-wide text-[#8CE63C] mb-2">{title}</h3>
    <div className="rounded-lg border border-[#232f25] bg-[#111712] px-4 py-1">{children}</div>
  </div>
);

const fmtDateTime = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

function InquiriesPanel({ input, onCount }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeF, setTypeF] = useState("All");
  const [statusF, setStatusF] = useState("All");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inquiries", {
        params: { search: search || undefined, type: typeF, status: statusF },
      });
      setItems(data.items);
      if (onCount) onCount(data.new);
    } catch {
      toast.error("Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  }, [search, typeF, statusF, onCount]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}/status`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
      toast.success(`Status → ${status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <input data-testid="inq-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, message…" className={cx(input, "flex-1")} />
        <select data-testid="inq-type-filter" value={typeF} onChange={(e) => setTypeF(e.target.value)} className={cx(input, "cursor-pointer")}>
          <option value="All">All Types</option>
          <option value="Special Request">Special Request</option>
          <option value="Contact">Contact</option>
        </select>
        <select data-testid="inq-status-filter" value={statusF} onChange={(e) => setStatusF(e.target.value)} className={cx(input, "cursor-pointer")}>
          <option value="All">All Statuses</option>
          {INQUIRY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="rounded-xl border border-[#232f25] bg-[#111712] overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid="admin-inquiries-table" className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#232f25] text-left">
                {["Date", "Type", "Name", "Email", "Phone", "Summary", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-[#69776b] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-[#69776b]">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-[#69776b]">No inquiries found.</td></tr>
              ) : (
                items.map((x) => (
                  <tr key={x.id} className="border-b border-[#1a211b] hover:bg-[#0d120e] transition-colors">
                    <td className="px-4 py-3 text-[#9EABA0] whitespace-nowrap">{fmtDateTime(x.created_at)}</td>
                    <td className="px-4 py-3"><span className={cx("px-2 py-1 rounded text-[10px] font-semibold border", x.type === "Special Request" ? "bg-[#221938] border-[#C084FC] text-[#E9D5FF]" : "bg-[#0E2433] border-[#38BDF8] text-[#7DD3FC]")}>{x.type}</span></td>
                    <td className="px-4 py-3 text-white font-medium whitespace-nowrap">{x.name}</td>
                    <td className="px-4 py-3 text-[#9EABA0]">{x.email}</td>
                    <td className="px-4 py-3 text-[#9EABA0] whitespace-nowrap">{x.phone || "—"}</td>
                    <td className="px-4 py-3 text-[#9EABA0] max-w-[220px] truncate">{x.subject || x.unit_type || x.message}</td>
                    <td className="px-4 py-3">
                      <select data-testid={`inq-status-select-${x.id}`} value={x.status} onChange={(e) => changeStatus(x.id, e.target.value)} className="bg-[#0d120e] border border-[#232f25] rounded-md px-2 py-1 text-xs text-[#f4f7f4] cursor-pointer focus:border-[#8CE63C] outline-none">
                        {INQUIRY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button data-testid={`inq-view-${x.id}`} onClick={() => setSelected(x)} className="text-xs font-semibold text-[#8CE63C] hover:underline">View</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-[#232f25] bg-[#0a0d0a] erp-fade-up">
            <div className="sticky top-0 bg-[#0a0d0a]/95 backdrop-blur border-b border-[#232f25] px-6 py-4 flex items-center justify-between">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#8CE63C]">{selected.type}</span>
                <h2 className="font-display text-xl font-black text-white uppercase tracking-tight">{selected.name}</h2>
              </div>
              <button data-testid="inq-detail-close" onClick={() => setSelected(null)} className="text-[#9EABA0] hover:text-white text-2xl leading-none px-2">×</button>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm">
              {[
                ["Submitted", fmtDateTime(selected.created_at)],
                ["Email", selected.email],
                ["Phone", selected.phone],
                ["Subject", selected.subject],
                ["Unit Type", selected.unit_type],
                ["Intended Use", selected.intended_use],
                ["Quantity", selected.quantity],
                ["Budget", selected.budget],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-3 border-b border-[#1a211b] pb-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-[#69776b]">{k}</div>
                  <div className="col-span-2 text-[#f4f7f4] break-words">{v}</div>
                </div>
              ))}
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#69776b] mb-1">Message</div>
                <p className="text-[#f4f7f4] whitespace-pre-wrap leading-relaxed">{selected.message}</p>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-2">
                {INQUIRY_STATUSES.map((s) => (
                  <button key={s} onClick={() => changeStatus(selected.id, s)} className={cx("px-3 py-1.5 rounded text-[11px] font-semibold border transition-colors", selected.status === s ? "bg-[#8CE63C] border-[#8CE63C] text-[#080b08]" : "bg-[#0d120e] border-[#232f25] text-[#9EABA0] hover:border-[#8CE63C]/50")}>{s}</button>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <a href={`mailto:${selected.email}`} className="flex-1 text-center px-4 py-2.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-bold hover:bg-[#9df04f] transition-colors">Reply by Email</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
